import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { TypographyWrapper } from "@waytek/MobiusOverrides/Link/Link";
import { cartLineCardExpandedStyles } from "@waytek/Overrides/Widgets/Cart/CartLineCardCondensed";
import { colorStrings, fontFamily, fontFamilyAlt, mediaQueriesBP } from "@waytek/Utilities/variables";
import { css } from "styled-components";

const localColors = {
    globalIndustrialTaupe: getColor("common.global.industrialTaupe"),
    globalDarkerGray: getColor("common.global.darkerGray"),
    globalWhite: getColor("common.global.white"),
    globalSecondaryGray: getColor("common.global.secondaryGray"),
    globalGuardianBlue: getColor("common.global.guardianBlue"),
};

// start general styles for columns

cartLineCardExpandedStyles.container = {
    ...cartLineCardExpandedStyles.container,
    css: css`
        &&& {
            > div {
                width: 100%;
                margin: 0;
                padding: 15px 10px 17px;
                border-bottom: 1px solid ${localColors.globalIndustrialTaupe};
                align-items: flex-start;

                ${mediaQueriesBP.minMD} {
                    padding: 15px 10px 20px;
                }
            }
        }
    `,
};

cartLineCardExpandedStyles.productImageGridItem = {
    ...cartLineCardExpandedStyles.productImageGridItem,
    width: [3, 3, 3, 3, 3],
    css: css`
        &&& {
            padding: 10px;
            border: 1px solid ${localColors.globalIndustrialTaupe};
            display: flex;
            justify-content: center;
            align-items: center;
            max-width: 172px;
            max-height: 129px;

            img {
                aspect-ratio: 4/3;
                width: 100%;
                height: 100%;
                object-fit: contain;
            }
        }
    `,
};

cartLineCardExpandedStyles.cartLineInfoGridItem = {
    ...cartLineCardExpandedStyles.cartLineInfoGridItem,
    width: [9, 9, 6, 6, 6],
    css: css`
        &&& {
            flex-grow: 1;
            max-width: 100%;
            padding-top: 0;
            padding-left: 15px;

            ${mediaQueriesBP.minMD} {
                padding-left: 20px;
                padding-bottom: 0;
            }
        }
    `,
};

cartLineCardExpandedStyles.removeCartLineGridItem = {
    ...cartLineCardExpandedStyles.removeCartLineGridItem,
    width: [12, 12, 3, 3, 3],
    css: css`
        padding-top: 9px;
        padding-right: 0;
        padding-bottom: 0;

        ${mediaQueriesBP.maxMD} {
            padding-left: 0;
        }
    `,
};
// end general styles for columns

cartLineCardExpandedStyles.productInfoContainer = {
    ...cartLineCardExpandedStyles.productInfoContainer,
    css: css`
        ${mediaQueriesBP.maxMD} {
            position: relative;
        }
    `,
};

// start styles second column: product details
cartLineCardExpandedStyles.productBrand = {
    ...cartLineCardExpandedStyles.productBrand,
    nameLink: {
        css: css`
            margin-bottom: 10px;

            span {
                font-size: 14px;
                font-weight: normal;
            }

            ${mediaQueriesBP.maxMD} {
                width: 80%;
            }
        `,
    },
};

cartLineCardExpandedStyles.productDescription = {
    ...cartLineCardExpandedStyles.productDescription,
    productDetailLink: {
        typographyProps: {
            size: 16,
        },
        css: css`
            font-size: 16px;
            font-weight: bold;
            font-family: ${fontFamily};
            font-stretch: normal;
            font-style: normal;
            line-height: 1.5;
            letter-spacing: normal;
            &&& {
                span {
                    border: 0;
                    text-decoration: none;

                    &:hover {
                        span {
                            border: 0;
                            text-decoration: underline;
                        }
                    }
                }
            }

            ${mediaQueriesBP.maxMD} {
                width: 80%;
            }
        `,
    },
};

cartLineCardExpandedStyles.productPartNumbersGridItem = {
    ...cartLineCardExpandedStyles.productPartNumbersGridItem,
    css: css`
        padding-top: 0;
        flex-direction: column;
        ${mediaQueriesBP.maxMD} {
            display: none;
        }
    `,
};

cartLineCardExpandedStyles.productPartNumbers = {
    ...cartLineCardExpandedStyles.productPartNumbers,
    erpNumberLabelText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
            margin-right: 5px;
            white-space: nowrap;
        `,
    },
    erpNumberValueText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
        `,
    },

    manufacturerItemLabelText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
            margin-right: 5px;
            white-space: nowrap;
        `,
    },
    manufacturerItemValueText: {
        css: css`
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
        `,
    },
};

cartLineCardExpandedStyles.customerWrapper = {
    ...cartLineCardExpandedStyles.customerWrapper,
    css: css`
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        margin-top: 10px;
        margin-bottom: 10px;
    `,
};

cartLineCardExpandedStyles.customerNumber = {
    ...cartLineCardExpandedStyles.customerNumber,
    customerLabel: {
        size: "14px",
        weight: 600,
        css: css`
            min-width: 88px;
        `,
    },
};

cartLineCardExpandedStyles.schedulesShipmentLinkWrapper = {
    ...cartLineCardExpandedStyles.schedulesShipmentLinkWrapper,
    css: css`
        display: none;
        align-items: center;

        ${mediaQueriesBP.minMD} {
            display: inline-flex;
            margin-top: 5px;
        }
    `,
};

cartLineCardExpandedStyles.cartLineNotesGridItem = {
    ...cartLineCardExpandedStyles.cartLineNotesGridItem,
    css: css`
        padding-top: 0;
        button {
            font-family: ${fontFamilyAlt};
            font-size: 14px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
        }

        ${mediaQueriesBP.maxMD} {
            display: none;
        }
    `,
};

cartLineCardExpandedStyles.cartLineNotesGridItemMobile = {
    ...cartLineCardExpandedStyles.cartLineNotesGridItemMobile,
    width: 12,
    css: css`
        padding-top: 0;

        ${mediaQueriesBP.minMD} {
            display: none;
        }
    `,
};

cartLineCardExpandedStyles.productInfoGridItem = {
    ...cartLineCardExpandedStyles.productInfoGridItem,
    width: 12,
};

// end styles second column: product details

// start styles last column: prices - subtotal - availability
cartLineCardExpandedStyles.subtotalColumn = {
    ...cartLineCardExpandedStyles.subtotalColumn,
    width: [12, 12, 9, 9, 9],
    css: css`
        padding-right: 0;
        ${mediaQueriesBP.minMD} {
            padding-left: 0;
            padding-right: 0;
        }
    `,
};

cartLineCardExpandedStyles.productPriceAndAvailabilityGridItem = {
    ...cartLineCardExpandedStyles.productPriceAndAvailabilityGridItem,
    width: 12,
    css: css`
        flex-direction: column;

        ${mediaQueriesBP.minMD} {
            padding-left: 0;
            padding-right: 0;
        }

        &&& {
            section {
                display: flex;
                flex-direction: row;
                align-items: center;
                font-size: 14px;
                font-family: ${fontFamilyAlt};
                color: ${localColors.globalSecondaryGray};

                ${mediaQueriesBP.minMD} {
                    justify-content: flex-end;
                }

                ${mediaQueriesBP.maxMD} {
                    order: 4;
                    margin: 5px 0;
                }

                legend {
                    margin-right: 5px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: ${fontFamilyAlt};
                    padding: 0;
                    color: ${localColors.globalSecondaryGray};
                }

                span {
                    font-size: 14px;
                    font-weight: normal;
                    font-family: ${fontFamilyAlt};
                    color: ${localColors.globalSecondaryGray};
                }
            }
        }
    `,
};

cartLineCardExpandedStyles.quantityWrapper = {
    ...cartLineCardExpandedStyles.quantityWrapper,
    css: css`
        display: flex;
        width: 100%;
        margin-top: 5px;
        ${mediaQueriesBP.minMD} {
            justify-content: flex-end;
            flex-direction: column;
        }

        ${mediaQueriesBP.maxMD} {
            order: 5;
        }

        > div {
            flex-direction: row;
            align-items: center;
            ${mediaQueriesBP.minMD} {
                justify-content: flex-end;
            }

            ${mediaQueriesBP.maxMD} {
                width: 99px;
                margin-right: 10px;
            }

            div {
                &&& {
                    margin-top: 0;
                }
            }
        }

        &&& {
            label {
                font-size: 14px;
                margin-right: 5px;
                font-weight: 600;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.71;
                letter-spacing: normal;
                text-align: right;
                text-transform: unset;
                ${mediaQueriesBP.maxMD} {
                    display: none;
                }

                + div {
                    margin-top: 0;
                }
            }

            input {
                height: 40px;
                width: 90px;

                ${mediaQueriesBP.maxMD} {
                    width: 99px;
                }
            }
        }
    `,
};

cartLineCardExpandedStyles.subtotalWrapper = {
    ...cartLineCardExpandedStyles.subtotalWrapper,
    css: css`
        justify-content: flex-end;
        display: flex;
        width: 100%;
        margin-top: 5px;

        p,
        span {
            font-size: 14px;
            font-weight: 600;
            font-stretch: normal;
            font-style: normal;
            line-height: 1.71;
            letter-spacing: normal;
            text-align: right;
            font-family: ${fontFamilyAlt};
            color: ${localColors.globalSecondaryGray};
        }

        ${mediaQueriesBP.maxMD} {
            margin: 0;
            text-align: left;
            justify-content: flex-start;
        }
    `,
};

cartLineCardExpandedStyles.subtotalLabel = {
    ...cartLineCardExpandedStyles.subtotalLabel,
    css: css`
        ${mediaQueriesBP.maxMD} {
            display: none;
        }
    `,
};

cartLineCardExpandedStyles.productMinQtyStockItem = {
    ...cartLineCardExpandedStyles.productMinQtyStockItem,
    css: css`
        margin: 0;
        width: 100%;
        text-align: right;

        ${mediaQueriesBP.maxMD} {
            order: 2;
            text-align: left;
        }
    `,
};

cartLineCardExpandedStyles.productStockWrapper = {
    ...cartLineCardExpandedStyles.productStockWrapper,
    css: css`
        width: 100%;
        text-align: right;

        ${mediaQueriesBP.maxMD} {
            display: none;
        }
    `,
};

cartLineCardExpandedStyles.productMinQtyStockLabel = {
    ...cartLineCardExpandedStyles.productMinQtyStockLabel,
    weight: 600,
    lineHeight: 1.71,
    css: css`
        flex: 0 0 auto;
        font-size: 14px;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        padding-right: 0.4em;
    `,
};

cartLineCardExpandedStyles.productMinQtyStockValue = {
    ...cartLineCardExpandedStyles.productMinQtyStockValue,
    weight: "normal",
    lineHeight: 1.71,
    css: css`
        flex: 1 1 auto;
        min-width: 0;
        font-size: 14px;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
    `,
};

cartLineCardExpandedStyles.addToListWrapper = {
    ...cartLineCardExpandedStyles.addToListWrapper,
    css: css`
        justify-content: flex-end;
        display: flex;
        width: 100%;

        button {
            margin-top: 0;
        }

        ${mediaQueriesBP.maxMD} {
            order: 6;
            justify-content: space-between;
            margin-top: 10px;
        }
    `,
};

cartLineCardExpandedStyles.removeIconColumn = {
    ...cartLineCardExpandedStyles.removeIconColumn,
    width: [0, 0, 3, 3, 3],
    css: css`
        padding-right: 0;
        justify-content: flex-end;
        padding-top: 0;

        svg {
            height: 24px;
            width: 24px;
            color: ${localColors.globalGuardianBlue};
            stroke-width: 1.3;

            ${mediaQueriesBP.maxMD} {
                display: none;
            }
        }
    `,
};

cartLineCardExpandedStyles.removeIconColumnMobile = {
    ...cartLineCardExpandedStyles.removeIconColumnMobile,
    css: css`
        padding-right: 0;
        justify-content: flex-end;
        padding-top: 0;
        margin-right: -10px;
        margin-top: -5px;
        position: absolute;
        right: 0;
        top: 0;

        ${mediaQueriesBP.minMD} {
            display: none;
        }

        svg {
            height: 24px;
            width: 24px;
            color: ${localColors.globalGuardianBlue};
            stroke-width: 1.3;
        }
    `,
};

cartLineCardExpandedStyles.productNumbersMobile = {
    ...cartLineCardExpandedStyles.productNumbersMobile,
    css: css`
        padding-top: 10px;
        order: 1;
        div {
            padding-top: 0;
            padding-bottom: 0;
        }
        ${mediaQueriesBP.minMD} {
            display: none;
        }
    `,
};
// end styles last column: prices - subtotal - availability

// modal styles
cartLineCardExpandedStyles.modalTopSection = {
    ...cartLineCardExpandedStyles.modalTopSection,
    css: css`
        padding: 25px 15px;
    `,
};

cartLineCardExpandedStyles.modalImageGridItem = {
    ...cartLineCardExpandedStyles.modalImageGridItem,
    width: [4, 4, 2, 2, 2],
    css: css`
        &&& {
            padding: 10px;
            border: 1px solid rgb(184 158 125 / 0.1);
            display: flex;
            justify-content: center;
            align-items: center;
            max-width: 120px;
            max-height: 90px;

            img {
                aspect-ratio: 4/3;
                object-fit: contain;
            }

            ${mediaQueriesBP.maxMD} {
                max-width: 100px;
                max-height: 75px;
            }
        }
    `,
};

cartLineCardExpandedStyles.modalProductDescriptionGridItem = {
    ...cartLineCardExpandedStyles.modalProductDescriptionGridItem,
    width: [8, 8, 6, 6, 6],
    css: css`
        flex-direction: column;
        padding-top: 0;

        > div {
            text-align: left;
        }

        .modalDescriptionItem {
            margin-bottom: 5px;
            display: block;

            ${mediaQueriesBP.maxMD} {
                display: none;

                &:nth-child(1) {
                    display: block;
                }
            }
        }
    `,
};

cartLineCardExpandedStyles.modalQuantityGridItem = {
    ...cartLineCardExpandedStyles.modalQuantityGridItem,
    width: [12, 12, 4, 4, 4],
    css: css`
        flex-direction: column;
        align-items: flex-end;
        padding-top: 0;
        padding-right: 0;

        .mobileInfo {
            display: none;
        }

        ${mediaQueriesBP.maxMD} {
            align-items: flex-start;
            padding-left: 0;

            .mobileInfo {
                display: block;
                text-align: left;
            }

            .productNumber {
                order: 1;
            }

            .productMinQtyStockItem {
                order: 2;
                margin-bottom: 5px;
            }

            .stockinfo {
                order: 3;
            }

            .priceInfo {
                order: 4;
            }
        }
    `,
};

cartLineCardExpandedStyles.modalPriceWrapper = {
    ...cartLineCardExpandedStyles.modalPriceWrapper,
    css: css`
        flex-direction: column;
        ${mediaQueriesBP.minMD} {
            padding-left: 0;
            padding-right: 0;
        }

        &&& {
            section {
                display: flex;
                flex-direction: row;
                align-items: center;
                font-size: 14px;
                font-family: ${fontFamilyAlt};
                color: ${localColors.globalSecondaryGray};
                ${mediaQueriesBP.minMD} {
                    justify-content: flex-end;
                }

                ${mediaQueriesBP.maxMD} {
                    order: 4;
                    margin: 5px 0;
                }

                legend {
                    margin-right: 5px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: ${fontFamilyAlt};
                    padding: 0;
                    color: ${localColors.globalSecondaryGray};
                }

                span {
                    font-size: 14px;
                    font-weight: normal;
                    font-family: ${fontFamilyAlt};
                    color: ${localColors.globalSecondaryGray};
                }
            }
        }
    `,
};

cartLineCardExpandedStyles.modalQuantityWrapper = {
    ...cartLineCardExpandedStyles.modalQuantityWrapper,
    css: css`
        display: flex;
        width: 100%;
        margin-top: 5px;
        ${mediaQueriesBP.minMD} {
            justify-content: flex-end;
            flex-direction: column;
            text-align: right;
        }

        ${mediaQueriesBP.maxMD} {
            order: 5;
            margin-top: 0;
        }

        > div {
            flex-direction: row;
            align-items: center;
            ${mediaQueriesBP.minMD} {
                justify-content: flex-end;
            }

            ${mediaQueriesBP.maxMD} {
                width: 99px;
                margin-right: 10px;
            }

            div {
                &&& {
                    margin-top: 0;
                }
            }
        }

        &&& {
            label {
                font-size: 14px;
                margin-right: 5px;
                font-weight: 600;
                font-stretch: normal;
                font-style: normal;
                line-height: 1.71;
                letter-spacing: normal;
                text-align: right;
                text-transform: unset;
                ${mediaQueriesBP.maxMD} {
                    display: none;
                }

                + div {
                    margin-top: 0;
                    width: auto;
                }
            }

            input {
                height: 40px;
                width: 90px;

                ${mediaQueriesBP.maxMD} {
                    width: 99px;
                }
            }
        }
    `,
};

cartLineCardExpandedStyles.modalSectionTitle = {
    ...cartLineCardExpandedStyles.modalSectionTitle,
    css: css`
        font-size: 18px;
        font-family: ${fontFamily};
        color: ${localColors.globalSecondaryGray};
        border-bottom: 3px solid ${localColors.globalIndustrialTaupe};
        font-weight: bold;
        padding-bottom: 4px;
        width: 100%;
    `,
};

cartLineCardExpandedStyles.modalBottomText = {
    ...cartLineCardExpandedStyles.modalBottomText,
    width: [12, 12, 8, 8, 8],
    css: css`
        span {
            font-size: 12px;
            font-weight: normal;
            font-stretch: normal;
            font-style: normal;
            line-height: normal;
            letter-spacing: normal;
            font-family: ${fontFamilyAlt};
            color: ${localColors.globalSecondaryGray};
        }
    `,
};

cartLineCardExpandedStyles.modalBottomControls = {
    ...cartLineCardExpandedStyles.modalBottomControls,
    width: [12, 12, 4, 4, 4],
    css: css`
        justify-content: flex-end;

        ${mediaQueriesBP.maxMD} {
            padding-top: 5px;
        }
    `,
};

cartLineCardExpandedStyles.modalBackButton = {
    ...cartLineCardExpandedStyles.modalBackButton,
    variant: "secondary",
    css: css`
        margin-right: 12px;
    `,
};

cartLineCardExpandedStyles.schedulesShipmentLink = {
    ...cartLineCardExpandedStyles.schedulesShipmentLink,
    typographyProps: {
        ...cartLineCardExpandedStyles.schedulesShipmentLink?.typographyProps,
        size: "14px",
        lineHeight: "18px",
    },
};

cartLineCardExpandedStyles.scheduleShipmentLinkMobile = {
    ...cartLineCardExpandedStyles.scheduleShipmentLinkMobile,
    css: css`
        display: inline-flex;
        align-items: center;

        ${mediaQueriesBP.minMD} {
            display: none;
        }
    `,
};

cartLineCardExpandedStyles.tooltip = {
    ...cartLineCardExpandedStyles.tooltip,
    iconProps: {
        ...cartLineCardExpandedStyles.tooltip?.iconProps,
        mergeCss: true,
        color: colorStrings.commonGlobalGuardianBlue,
        css: css`
            ${cartLineCardExpandedStyles.tooltip?.iconProps?.css}
            top: 0;
        `,
    },
    typographyProps: {
        mergeCss: true,
        css: css`
            ${cartLineCardExpandedStyles.tooltip?.typographyProps?.css}
            ${TypographyWrapper} {
                ${TypographyStyle} {
                    color: ${getColor(colorStrings.commonGlobalGuardianBlue)};
                }
            }
        `,
    },
    cssOverrides: {
        tooltipWrapper: css`
            ${cartLineCardExpandedStyles.tooltip?.cssOverrides?.tooltipWrapper}
            margin: 0 10px;
        `,
        tooltipClickable: css`
            ${cartLineCardExpandedStyles.tooltip?.cssOverrides?.tooltipClickable}
            padding: 0;
        `,
    },
};
